import { useMemo } from "react";
import { StudentSource } from "src/models/students";
import {
  useGetMyClassesQuery,
  useListClassesQuery,
} from "src/store/reducers/classes/classesApiSlice";

/** A class a student can be enrolled in or moved to. */
export interface StudentClassOption {
  id: string;
  name: string;
  schoolName?: string | null;
  source: StudentSource;
}

export interface StudentClassOptionsArgs {
  /** Teachers pick from their own classes; admins search every class in scope. */
  ownClasses: boolean;
  term: string;
  districtId?: string;
  schoolId?: string;
  /** Hides classes the student is already in, or the class being moved from. */
  exclude?: string[];
  /** Only classes the school created (move targets). */
  manualOnly?: boolean;
}

/**
 * Class options for the student pickers. Teachers filter `classes/mine` on the client; admins
 * search `GET /v1/classes` on the server, narrowed to the student's school.
 */
export const useStudentClassOptions = ({
  ownClasses,
  term,
  districtId,
  schoolId,
  exclude,
  manualOnly,
}: StudentClassOptionsArgs) => {
  const mine = useGetMyClassesQuery(undefined, { skip: !ownClasses });
  const scoped = useListClassesQuery(
    { districtId, schoolId, search: term || undefined, limit: 25 },
    { skip: ownClasses },
  );
  const excluded = (exclude ?? []).join(",");

  const data = useMemo<StudentClassOption[] | undefined>(() => {
    const skip = new Set(excluded ? excluded.split(",") : []);
    const needle = term.toLowerCase();
    const options: StudentClassOption[] | undefined = ownClasses
      ? mine.data
          ?.filter((c) => c.active !== false)
          .filter((c) => !needle || c.name.toLowerCase().includes(needle))
          .map((c) => ({ id: c.id, name: c.name, source: c.source }))
      : scoped.data?.items.map((c) => ({
          id: c.id,
          name: c.name,
          schoolName: c.schoolName,
          source: c.source,
        }));
    return options?.filter(
      (c) => !skip.has(c.id) && (!manualOnly || c.source === "manual"),
    );
  }, [ownClasses, mine.data, scoped.data, term, excluded, manualOnly]);

  const source = ownClasses ? mine : scoped;
  return {
    data,
    isLoading: source.isLoading,
    isFetching: source.isFetching,
  };
};

export default useStudentClassOptions;
